import { useCallback, useEffect, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { ErrorState, LoadingState } from "../../components/states";
import { PageIntro } from "../../components/admin/PageIntro";
import { SettingPanel, SettingSwitch } from "../../components/admin/SettingPanel";
import adminStyles from "../../components/admin/Admin.module.css";
import panel from "../../components/admin/AdminPanel.module.css";
import { Tooltip } from "../../components/shared/Tooltip";
import { useToast } from "../../components/toast/toastContext";
import { fetchFeatureFlags, updateFeatureFlag, type FeatureFlag } from "../../api/featureFlags";
import { fetchRoles, type RoleBundle } from "../../api/roles";

const SettingsWritePermission = "settings.write";

/** The feature that limits per-person vendor writes, which carries a caveat about Bedrock Model Management. */
const vendorWritesKey = "vendor-writes";

/** The feature that decides who may be issued Bedrock keys and see the Bedrock tooling. It has a "not configured yet" state and a caveat. */
const bedrockInferenceKey = "bedrock-inference";

/**
 * The "Features" tab of System Settings: every feature the portal has, with a switch for whether it is on and the roles it applies to.
 * A feature applies to a person only when it is on and the person holds one of its roles, whether assigned or automatic ("Everyone" is
 * the automatic role everybody holds). Saves and refusals are reported as toasts, never as a line in the page.
 */
export function FeaturesContent(): React.JSX.Element {
  const { t } = useTranslation();
  const [state, setState] = useState<{ kind: "loading" } | { kind: "loaded"; features: FeatureFlag[]; roles: RoleBundle[] | null } | { kind: "error"; message: string }>({ kind: "loading" });

  const load = useCallback(async () => {
    setState({ kind: "loading" });
    const [features, roles] = await Promise.all([fetchFeatureFlags(), fetchRoles()]);
    if (features.kind === "ok") {
      // Listing the roles needs roles.administer. Without it the roles a feature has are still shown, but cannot be changed.
      setState({ kind: "loaded", features: features.response, roles: roles.kind === "ok" ? roles.response : null });
    } else if (features.kind === "denied") {
      setState({ kind: "error", message: t("admin.denied.description", { permissions: SettingsWritePermission }) });
    } else {
      setState({ kind: "error", message: features.message });
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const replace = (saved: FeatureFlag) =>
    setState((current) => (current.kind === "loaded" ? { ...current, features: current.features.map((feature) => (feature.key === saved.key ? saved : feature)) } : current));

  if (state.kind === "loading") {
    return <LoadingState label={t("admin.features.loading")} />;
  }

  if (state.kind === "error") {
    return <ErrorState title={t("admin.features.errorTitle")} description={state.message} onRetry={() => void load()} />;
  }

  return (
    <section className={adminStyles.section}>
      <PageIntro>{t("admin.features.explanation")}</PageIntro>
      {state.features.map((feature) => (
        <FeaturePanel key={feature.key} feature={feature} roles={state.roles} onSaved={replace} />
      ))}
    </section>
  );
}

/** One feature: its switch, the roles it applies to, and a save for the roles. */
function FeaturePanel({ feature, roles, onSaved }: { feature: FeatureFlag; roles: RoleBundle[] | null; onSaved: (saved: FeatureFlag) => void }): React.JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const errorId = useId();
  const [selected, setSelected] = useState<string[]>(feature.roles.map((role) => role.id));
  const [pending, setPending] = useState(false);

  const savedIds = feature.roles.map((role) => role.id);
  const changed = selected.length !== savedIds.length || selected.some((id) => !savedIds.includes(id));
  const label = t(`admin.features.names.${feature.key}`, { defaultValue: feature.key });

  const save = async (enabled: boolean, roleIds: string[]) => {
    setPending(true);
    const result = await updateFeatureFlag(feature.key, enabled, roleIds);
    setPending(false);
    if (result.kind === "ok") {
      onSaved(result.response);
      setSelected(result.response.roles.map((role) => role.id));
      toast.show(t("admin.features.saved", { name: label }), "success");
    } else if (result.kind === "denied") {
      toast.show(t("admin.denied.description", { permissions: SettingsWritePermission }), "error");
    } else {
      toast.show(result.message, "error");
    }
  };

  const toggle = (id: string, on: boolean) => setSelected((current) => (on ? [...current, id] : current.filter((candidate) => candidate !== id)));
  const noRoles = feature.enabled && savedIds.length === 0;

  return (
    <SettingPanel
      id={`feature-${feature.key}`}
      title={label}
      intro={feature.description}
      headerActions={<SettingSwitch label={t("admin.features.toggleLabel", { name: label })} checked={feature.enabled} onChange={(next) => void save(next, savedIds)} disabled={pending} />}
    >
      <fieldset className={adminStyles.permissionFieldset} aria-describedby={roles === null ? errorId : undefined}>
        <legend>{t("admin.features.rolesLegend", { name: label })}</legend>
        <div className={adminStyles.permissionGrid}>
          {(roles ?? feature.roles.map((role) => ({ id: role.id, name: role.name, description: "", permissionKeys: [], isAutomatic: false }))).map((role) => (
            <label key={role.id} className={adminStyles.permissionOption}>
              <input type="checkbox" checked={selected.includes(role.id)} disabled={pending || roles === null} onChange={(event) => toggle(role.id, event.target.checked)} />
              <Tooltip label={role.description}>
                <span>{role.name}</span>
              </Tooltip>
            </label>
          ))}
        </div>
        {roles === null && (
          <p id={errorId} className={adminStyles.note}>
            {t("admin.features.rolesUnavailable")}
          </p>
        )}
      </fieldset>
      <div>
        <button type="button" className="button button-secondary" disabled={pending || roles === null || !changed} onClick={() => void save(feature.enabled, selected)}>
          {t("admin.features.saveRoles")}
        </button>
      </div>
      <div className={panel.footnotes}>
        {feature.key === bedrockInferenceKey && (feature.configuredAt ?? null) === null && <p>{t("admin.features.bedrockInferenceNotConfigured")}</p>}
        <p>{feature.enabled ? (noRoles ? t("admin.features.stateOnNoRoles") : t("admin.features.stateOn")) : t("admin.features.stateOff")}</p>
        {feature.key === vendorWritesKey && <p>{t("admin.features.vendorWritesCaveat")}</p>}
        {feature.key === bedrockInferenceKey && <p>{t("admin.features.bedrockInferenceCaveat")}</p>}
        <p>
          {feature.updatedByName !== null
            ? t("admin.features.updatedBy", { name: feature.updatedByName, updatedAt: new Intl.DateTimeFormat(navigator.language, { dateStyle: "long", timeStyle: "short" }).format(new Date(feature.updatedAt)) })
            : t("admin.features.neverChanged")}
        </p>
      </div>
    </SettingPanel>
  );
}
